const DEFAULT_POSITIONS = ['tor', 'abwehr', 'mittelfeld', 'sturm', 'tw'];

function isStatLine(line) {
    const t = String(line || '').trim();
    return t === '-' || /^-?\d+$/.test(t);
}

function buildPositionSet(positionNames = []) {
    const set = new Set(DEFAULT_POSITIONS);
    positionNames.forEach((name) => {
        if (name) set.add(String(name).toLowerCase());
    });
    return set;
}

function isPositionLine(line, positionSet) {
    return positionSet.has(String(line || '').trim().toLowerCase());
}

/**
 * Parse pasted squad list. Last numeric line per player = jersey number.
 */
export function parsePlayerList(text, positionNames = []) {
    const positionSet = buildPositionSet(positionNames);
    const lines = String(text || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

    const players = [];
    let i = 0;

    while (i < lines.length) {
        let name = lines[i];
        let position = null;
        i += 1;

        while (i < lines.length && !isStatLine(lines[i])) {
            const line = lines[i];
            if (line === name) {
                i += 1;
                continue;
            }
            if (isPositionLine(line, positionSet)) {
                position = line;
                i += 1;
                break;
            }
            name = line;
            i += 1;
        }

        const stats = [];
        while (i < lines.length && isStatLine(lines[i])) {
            stats.push(lines[i]);
            i += 1;
        }

        if (!stats.length) continue;

        const jerseyRaw = stats[stats.length - 1];
        const num = jerseyRaw === '-' ? null : parseInt(jerseyRaw, 10);

        players.push({
            fullName: name,
            position,
            num: Number.isFinite(num) ? num : null,
        });
    }

    return players;
}

export function normalizeName(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

export function splitFullName(fullName) {
    const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return { prename: '', surname: '' };
    if (parts.length === 1) return { prename: '', surname: parts[0] };
    return {
        prename: parts.slice(0, -1).join(' '),
        surname: parts[parts.length - 1],
    };
}

export function findPlayerByName(players, fullName) {
    const target = normalizeName(fullName);
    if (!target) return null;

    const { prename, surname } = splitFullName(fullName);

    return players.find((p) => {
        const full = normalizeName(`${p.prename || ''} ${p.surname}`.trim());
        const reversed = normalizeName(`${p.surname} ${p.prename || ''}`.trim());
        if (full === target || reversed === target) return true;
        return normalizeName(p.prename) === normalizeName(prename)
            && normalizeName(p.surname) === normalizeName(surname);
    }) || null;
}

export function resolvePositionId(positionLabel, positions) {
    if (!positionLabel || !positions?.length) return null;
    const key = positionLabel.toLowerCase();
    const exact = positions.find((p) => p.name?.toLowerCase() === key);
    if (exact) return exact._id;
    const partial = positions.find((p) => {
        const name = p.name?.toLowerCase() || '';
        return name.startsWith(key) || key.startsWith(name);
    });
    return partial?._id || null;
}

const PASTE_SKIP_HEADERS = new Set(['rnr.', 'rnr', 'spieler', 'position']);
const PASTE_ROLE_HINTS = new Set(['tor', 'abwehr', 'mittelfeld', 'sturm', 'ersatz']);
const PASTE_STAT_LINE = /^-$|^0+$/;
const PASTE_NUM_LINE = /^\d{1,2}$/;

function isPasteStatLine(line) {
    const value = String(line || '').trim();
    return PASTE_STAT_LINE.test(value);
}

function isPasteJerseyLine(line, nextLine) {
    const value = String(line || '').trim();
    if (!PASTE_NUM_LINE.test(value)) return false;
    if (!nextLine) return true;
    if (isPasteStatLine(nextLine)) return false;
    if (isPasteRoleHint(nextLine)) return false;
    return true;
}

function isPasteRoleHint(line) {
    return PASTE_ROLE_HINTS.has(String(line || '').trim().toLowerCase());
}

/**
 * Parse pasted lineup where jersey number comes first (portal format).
 * Example block: 01 / Noah Ender / Noah Ender / Tor / 0 0 0 0
 */
export function parseLineupPasteNumberFirst(text) {
    const lines = String(text || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line && !PASTE_SKIP_HEADERS.has(line.toLowerCase()));

    const players = [];
    let i = 0;

    while (i < lines.length) {
        if (!isPasteJerseyLine(lines[i], lines[i + 1])) {
            i += 1;
            continue;
        }

        const num = parseInt(lines[i], 10);
        i += 1;

        let rawName = '';
        let isCaptain = false;

        while (i < lines.length) {
            const line = lines[i];
            if (isPasteJerseyLine(line, lines[i + 1])) break;
            if (isPasteRoleHint(line)) break;
            if (isPasteStatLine(line) && rawName) break;

            if (/(\(K\))/i.test(line)) isCaptain = true;
            const cleaned = line.replace(/\s*\(K\)\s*/gi, ' ').trim();
            if (!rawName) rawName = cleaned;
            else if (cleaned.toLowerCase() !== rawName.toLowerCase()) rawName = cleaned;
            i += 1;
        }

        let roleHint = null;
        if (i < lines.length && isPasteRoleHint(lines[i])) {
            roleHint = lines[i];
            i += 1;
        }

        while (i < lines.length && isPasteStatLine(lines[i])) {
            i += 1;
        }

        if (!rawName) continue;

        const { prename, surname } = splitFullName(rawName);
        players.push({
            num: Number.isFinite(num) ? num : null,
            fullName: rawName,
            prename,
            surname,
            roleHint,
            position: roleHint,
            isCaptain,
            isStarter: roleHint ? roleHint.toLowerCase() !== 'ersatz' : true,
        });
    }

    return players;
}

export function buildOwnTeamPastePreview(parsed, playertools = []) {
    const rosterPlayers = playertools
        .map((pt) => pt.plid)
        .filter((p) => p?.surname);

    return parsed.map((row) => {
        const { prename, surname } = splitFullName(row.fullName);
        const match = findPlayerByName(rosterPlayers, row.fullName);
        const pt = match
            ? playertools.find((entry) => toStr(entry.plid?._id || entry.plid) === toStr(match._id))
            : null;

        return {
            ...row,
            prename,
            surname,
            status: match ? 'matched' : 'unknown',
            message: match ? 'Im Kader erkannt' : 'Name wird mit Stammdaten abgeglichen',
            num: row.num ?? pt?.num ?? null,
        };
    });
}

function toStr(v) {
    return (v?._id || v || '').toString();
}

export function buildImportPreview(parsedPlayers, existingPlayers, existingAssignments, positions) {
    const assignmentKey = (plid) => `${plid}`;

    const assignedPlids = new Set(
        (existingAssignments || []).map((a) => assignmentKey(a.plid?._id || a.plid))
    );

    return parsedPlayers.map((row) => {
        const { prename, surname } = splitFullName(row.fullName);
        const match = findPlayerByName(existingPlayers, row.fullName);
        const posid = resolvePositionId(row.position, positions);

        let status = 'new';
        let message = 'Spieler + Kader-Eintrag anlegen';

        if (match) {
            if (assignedPlids.has(assignmentKey(match._id))) {
                status = 'assigned';
                message = 'Bereits im Kader';
            } else {
                status = 'existing';
                message = 'Nur Kader-Eintrag anlegen';
            }
        }

        return {
            ...row,
            prename,
            surname,
            posid,
            matchedPlayer: match,
            status,
            message,
        };
    });
}
